import React, { useState, useEffect } from 'react';
import IntlMessages from '@iso/components/utility/intlMessages';
import AssetModalWrapper from './AssetModal.styles';
import { Form, Input, Button, Select, Row, Col, DatePicker, InputNumber, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { propertyService, attachment as attachmentService, assetService } from '@iso/services';
// import { primaryObjectServiceV2 } from '@iso/services/primaryobject.service.v2';
// import PrimaryObjectSelect from "@iso/components/Select/PrimaryObjectSelect";
import moment from 'moment';
import _ from 'lodash';
import { objectColors } from '@iso/constants/objectColors';
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from "@iso/lib/helpers/authHeader";

const { Option } = Select;

const EditLockOutHistoryModal = (props) => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const currentUser = useSelector((state) => state.user.user);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const [resolvedLocationId, setResolvedLocationId] = useState(null);

  useEffect(() => {
    if (props.visible) {
      loadUsers();
      loadPrimaryObjects();
    }
  }, [props.visible]);

  useEffect(() => {
    if (props.visible && props.historyData && locationOptions.length > 0) {
      initializeForm();
    }
  }, [props.visible, props.historyData, locationOptions]);

  const loadUsers = async () => {
    try {
      const response = await propertyService.getListUsersOfProperty(activePropertyId);
      if (response && response.users) {
        setUsers(response.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadPrimaryObjects = async () => {
    propertyService.viewProperty(activePropertyId).then((res) => {
      if (res.code === "0000") {
        setLocationOptions(res.primary_objects);
      }
    });
  };

  // Check if current user can unlock (is the User or Manager)
  const canUnlock = () => {
    if (!currentUser || !props.historyData?.metadata?.lock_out) {
      return false;
    }
    const lockOutData = props.historyData.metadata.lock_out;
    return currentUser.id === lockOutData.user_id || currentUser.id === lockOutData.manager_id;
  };

  // Check if current user is the manager
  const isManager = () => {
    if (!currentUser || !props.historyData?.metadata?.lock_out) {
      return false;
    }
    const lockOutData = props.historyData.metadata.lock_out;
    return currentUser.id === lockOutData.manager_id;
  };

  // Check if current user can edit lockout history (is the creator or manager)
  const canEditHistory = () => {
    if (!currentUser || !props.historyData?.metadata?.lock_out) {
      return false;
    }
    const lockOutData = props.historyData.metadata.lock_out;
    return currentUser.id === lockOutData.user_id || currentUser.id === lockOutData.manager_id;
  };

  const initializeForm = () => {
    if (props.historyData && props.historyData.metadata && props.historyData.metadata.lock_out) {
      const lockOutData = props.historyData.metadata.lock_out;

      // Resolve location value - could be ID or name
      let locationValue = null;
      if (lockOutData.location && locationOptions.length > 0) {
        // Try to find by ID first
        const locationById = locationOptions.find(loc => loc.id.toString() === lockOutData.location.toString());
        if (locationById) {
          locationValue = locationById.name; // Use name for display
          setSelectedLocation(locationById.name);
          setResolvedLocationId(locationById.id);
        } else {
          // Try to find by name
          const locationByName = locationOptions.find(loc => loc.name === lockOutData.location);
          if (locationByName) {
            locationValue = locationByName.name; // Use name for display
            setSelectedLocation(locationByName.name);
            setResolvedLocationId(locationByName.id);
          } else {
            // If not found, use as-is (might be text)
            setSelectedLocation(lockOutData.location);
            locationValue = lockOutData.location;
          }
        }
      }
      
      form.setFieldsValue({
        history: {
          datetime: lockOutData.datetime ? moment(lockOutData.datetime) : null,
          expiry_datetime: lockOutData.expiry_datetime ? moment(lockOutData.expiry_datetime) : null,
          reason: lockOutData.reason || '',
          location: locationValue,
          user_id: lockOutData.user_id || null,
          contact_number: lockOutData.contact_number || '',
          lock_number: lockOutData.lock_number || '',
          lock_colour: lockOutData.lock_colour || '',
          alarm_minutes: lockOutData.alarm_minutes || 30,
          manager_id: lockOutData.manager_id || null,
          status: lockOutData.status || 'locked_out',
          unlock_datetime: lockOutData.unlock_datetime ? moment(lockOutData.unlock_datetime) : moment(),
          unlock_reason: lockOutData.unlock_reason || '',
        }
      });

      // Set existing attachments if any
      if (props.historyData.attachments && props.historyData.attachments.length > 0) {
        const existingAttachmentIds = props.historyData.attachments.map(att => att.id);
        setAttachmentIds(existingAttachmentIds);
        
        // Set existing files for display
        const existingFiles = props.historyData.attachments.map((att, index) => ({
          uid: att.id,
          name: att.url ? att.url.split('/').pop() : `Attachment ${index + 1}`,
          status: 'done',
          url: att.full_path,
          response: {
            attachment: att
          }
        }));
        setUploadFiles(existingFiles);
      }
    }
  };

  const cancelModal = () => {
    props.onCancel();
    setLoading(false);
    form.resetFields();
    setAttachmentIds([]);
    setUploadFiles([]);
    setSelectedLocation(null);
    setLocationOptions([]);
    setResolvedLocationId(null);
  }

  const handleSubmit = () => {
    setLoading(true);

    form
      .validateFields()
      .then(values => {
        onStore(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
        setLoading(false);
      });
  }

  const onStore = async (values) => {
    try {
      // Prepare lock out data for update
      const lockOutData = {
        lock_out: {
          asset_name: props.historyData?.metadata?.lock_out?.asset_name || '',
          reason: values.history.reason || '',
          location: values.history.location || '',
          datetime: values.history.datetime.format('YYYY-MM-DD HH:mm:ss'),
          expiry_datetime: values.history.expiry_datetime.format('YYYY-MM-DD HH:mm:ss'),
          user_id: values.history.user_id || null,
          contact_number: values.history.contact_number || '',
          lock_number: values.history.lock_number || '',
          lock_colour: values.history.lock_colour || '',
          alarm_minutes: values.history.alarm_minutes || 30,
          manager_id: values.history.manager_id || null,
          description: values.history.description || '',
          attachment_ids: attachmentIds,
          status: values.history.status || 'locked_out',
          unlock_datetime: values.history.status === 'unlocked' && values.history.unlock_datetime ?
            values.history.unlock_datetime.format('YYYY-MM-DD HH:mm:ss') : null,
          unlock_reason: values.history.status === 'unlocked' ? (values.history.unlock_reason || '') : ''
        }
      };

      const response = await assetService.updateLockOut(lockOutData, props.historyData.id);
      
      if (response && (response.code === '0000' || response.code === 200)) {
        cancelModal();
        form.resetFields();
        if (props.onUpdate) {
          props.onUpdate(props.historyData.object_id, 'facilities');
        }
        // Trigger asset reload to update state and visual indicators
        if (props.onAssetUpdated) {
          props.onAssetUpdated();
        }
        // Trigger property reload to refresh all assets on map
        if (props.updatePropertyDetail) {
          props.updatePropertyDetail(activePropertyId);
        }
        message.success('Lock out updated successfully');
      } else {
        setLoading(false);
        message.error(response?.message || 'Lock out update failed');
      }
    } catch (error) {
      setLoading(false);
      console.error('Lock out update error:', error);
      message.error('Lock out update failed');
    }
  }

  const handleUserChange = (userId) => {
    // Auto-fill contact number when user is selected
    const selectedUser = users.find(user => user.id === userId);
    if (selectedUser && selectedUser.mobile_phone) {
      form.setFieldsValue({
        history: {
          ...form.getFieldValue('history'),
          contact_number: selectedUser.mobile_phone
        }
      });
    }
  };

  const renderUserSelect = () => {
    return (
      <Select
        placeholder="Select user"
        allowClear
        size="large"
        showSearch
        onChange={handleUserChange}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }>
        {users.map((user) => (
          <Option key={user.id} value={user.id}>
            {user.name}
          </Option>
        ))}
      </Select>
    );
  };

  const renderManagerSelect = () => {
    return (
      <Select placeholder="Select manager (optional)" allowClear size="large" showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }>
        {users.map((user) => (
          <Option key={user.id} value={user.id}>
            {user.name}
          </Option>
        ))}
      </Select>
    );
  };

  const renderLockColorOptions = () => {
    let options = [];
    objectColors.forEach((color, index) => {
      options.push(
        <Option key={index} value={color.value}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: color.color,
              border: '1px solid #d9d9d9',
              borderRadius: '2px'
            }}></div>
            <span>{color.label}</span>
          </div>
        </Option>
      );
    });
    return options;
  };

  const onFileChange = (response) => {
    if(response.file && response.file.status === "done"){
      handleUploadAttachmentSuccess(response.file.response.attachment);
    }

    setUploadFiles([...response.fileList]);
  };

  const handleUploadAttachmentSuccess = (attachment) => {
    setAttachmentIds(attachmentIds => [...attachmentIds, attachment.id]);
  }

  const handleRemoveAttachment = (attachment) => {
    const removedAttachmentId = attachment.response?.attachment?.id ?? attachment.id;

    if (removedAttachmentId) {
      attachmentService.destroy(removedAttachmentId);
    }
  }

  const handleLocationChange = (value) => {
    if (value) {
      // Find the selected location object by name
      const selectedLocationObj = locationOptions.find(loc => loc.name === value);
      if (selectedLocationObj) {
        setSelectedLocation(selectedLocationObj.name);
        setResolvedLocationId(selectedLocationObj.id);
      }
    } else {
      // Handle clearing the selection
      setSelectedLocation(null);
      setResolvedLocationId(null);
    }
  }

  const renderOptionObject = (objects) => {
    let options = [];
    _.forEach(objects, (object, index) => {
      options.push(
        <Option key={object.id} value={object.name}>
          {object.name}
        </Option>
      );
    })
    return (
      <Select
        placeholder="Select an object"
        allowClear
        size="large"
        onChange={handleLocationChange}
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {options}
      </Select>
    );
  };

  return(
    <AssetModalWrapper
      visible={props.visible}
      onCancel={cancelModal}
      maskClosable={true}
      title="Edit Lock Out History"
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          Cancel
        </Button>,
        <Button 
          key="submit" 
          className="btn-success" 
          type="primary" 
          onClick={handleSubmit} 
          loading={loading}
          disabled={!canEditHistory()}
        >
          Save
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        id="#edit-lockout-form"
      >
        <div className="form-body">
          {/* Asset Name (read-only) */}
          <Row>
            <Col xs={{span: 24}}>
              <Form.Item
                className="isoInputWrapper"
                label="Asset Name"
              >
                <Input 
                  size="large" 
                  value={(props.historyData && props.historyData.metadata && props.historyData.metadata.lock_out && props.historyData.metadata.lock_out.asset_name) || 'N/A'}
                  disabled
                  style={{ backgroundColor: '#f5f5f5' }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Date & Time and Expiry Date & Time */}
          <Row>
            <Col xs={{span: 24}} md={{span: 11}}>
              <Form.Item
                name={['history', 'datetime']}
                className="isoInputWrapper"
                label="Date & Time"
                rules={[
                  {
                    required: true,
                    message: 'Please select date and time',
                  },
                ]}
              >
                <DatePicker 
                  showTime 
                  size="large" 
                  style={{width: '100%'}}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
            <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
              <Form.Item
                name={['history', 'expiry_datetime']}
                className="isoInputWrapper"
                label="Expiry Date & Time"
                rules={[
                  {
                    required: true,
                    message: 'Please select expiry date and time',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const lockOutDate = getFieldValue(['history', 'datetime']);
                      if (!value || !lockOutDate || value.isAfter(lockOutDate)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Expiry must be after lock out date!'));
                    },
                  }),
                ]}
              >
                <DatePicker 
                  showTime 
                  size="large" 
                  style={{width: '100%'}}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Reason and Location */}
          <Row>
            <Col xs={{span: 24}} md={{span: 11}}>
              <Form.Item
                name={['history', 'reason']}
                className="isoInputWrapper"
                label="Reason"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Please enter a reason',
                  },
                ]}
              >
                <Input 
                  size="large" 
                  placeholder="Enter lock out reason"
                />
              </Form.Item>
            </Col>
            <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
              <Form.Item
                name={['history', 'location']}
                className="isoInputWrapper"
                label="Location"
                rules={[
                  {
                    required: true,
                    message: 'Please select a location',
                  },
                ]}
              >
                {renderOptionObject(locationOptions)}
              </Form.Item>
            </Col>
          </Row>

          {/* User and Contact */}
          <Row>
            <Col xs={{span: 24}} md={{span: 11}}>
              <Form.Item
                name={['history', 'user_id']}
                className="isoInputWrapper"
                label="User"
                rules={[
                  {
                    required: true,
                    message: 'Please select a user',
                  },
                ]}
              >
{renderUserSelect()}
              </Form.Item>
            </Col>
            <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
              <Form.Item
                name={['history', 'contact_number']}
                className="isoInputWrapper"
                label="Contact Number"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Please enter contact number',
                  },
                ]}
              >
                <Input 
                  size="large" 
                  placeholder="Contact number"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Lock Number and Color */}
          <Row>
            <Col xs={{span: 24}} md={{span: 11}}>
              <Form.Item
                name={['history', 'lock_number']}
                className="isoInputWrapper"
                label="Lock Number"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Please enter lock number',
                  },
                ]}
              >
                <Input 
                  size="large" 
                  placeholder="Lock identification number"
                />
              </Form.Item>
            </Col>
            <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
              <Form.Item
                name={['history', 'lock_colour']}
                className="isoInputWrapper"
                label="Lock Color"
              >
                <Select
                  size="large"
                  placeholder="Select lock color"
                  allowClear
                >
                  {renderLockColorOptions()}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Alarm and Manager */}
          <Row>
            <Col xs={{span: 24}} md={{span: 11}}>
              <Form.Item
                name={['history', 'alarm_minutes']}
                className="isoInputWrapper"
                label="Alarm Before Expiry (minutes)"
              >
                <InputNumber
                  size="large"
                  style={{ width: '100%' }}
                  min={1}
                  max={10080}
                  placeholder="Minutes before expiry"
                />
              </Form.Item>
            </Col>
            <Col xs={{span: 24}} md={{span: 11, offset: 2}}>
              <Form.Item
                name={['history', 'manager_id']}
                className="isoInputWrapper"
                label="Manager"
              >
{renderManagerSelect()}
              </Form.Item>
            </Col>
          </Row>

          {/* Status */}
          <Row>
            <Col xs={{span: 24}}>
              <Form.Item
                name={['history', 'status']}
                className="isoInputWrapper"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: 'Please select a status',
                  },
                ]}
              >
                {(
                  <Select size="large" placeholder="Select status" disabled={!canUnlock()}>
                    <Option value="locked_out">Locked Out</Option>
                    <Option value="unlocked">Remove Lock</Option>
                  </Select>
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Unlock Date & Time */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => {
              return prevValues['history']?.status !== currentValues['history']?.status;
            }}
          >
            {({ getFieldValue }) => {
              if (getFieldValue(['history', 'status']) === 'unlocked') {
                return (
                  <Row>
                    <Col xs={{span: 24}}>
                      <Form.Item
                        name={['history', 'unlock_datetime']}
                        className="isoInputWrapper"
                        label="Unlock Date & Time"
                        rules={[
                          {
                            required: true,
                            message: 'Please select unlock date and time',
                          },
                        ]}
                      >
                        <DatePicker
                          showTime
                          size="large"
                          style={{width: '100%'}}
                          format="YYYY-MM-DD HH:mm"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }
              return null;
            }}
          </Form.Item>

          {/* Unlock Reason - Required for Manager */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => {
              return prevValues['history']?.status !== currentValues['history']?.status;
            }}
          >
            {({ getFieldValue }) => {
              if (getFieldValue(['history', 'status']) === 'unlocked') {
                const unlockReasonValue = getFieldValue(['history', 'unlock_reason']) || '';

                return (
                  <Row>
                    <Col xs={{span: 24}}>
                      <Form.Item
                        name={['history', 'unlock_reason']}
                        className="isoInputWrapper"
                        label="Unlock Reason"
                        rules={canUnlock() ? [
                          {
                            required: isManager(),
                            whitespace: true,
                            message: 'Please enter unlock reason (required for Manager)',
                          },
                        ] : []}
                      >
                        {canUnlock() ? (
                          <Input.TextArea
                            size="large"
                            placeholder={isManager() ? "Enter reason for unlocking (required)" : "Enter reason for unlocking (optional)"}
                            rows={3}
                          />
                        ) : (
                          <Input.TextArea
                            size="large"
                            disabled
                            style={{ backgroundColor: '#f5f5f5' }}
                            value={unlockReasonValue}
                            rows={3}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }
              return null;
            }}
          </Form.Item>

          {/* Photos */}
          <Row>
            <Col xs={{span: 24}}>
              <Form.Item
                className="isoInputWrapper"
                label="Attach photos, videos, documents"
              >
                <Upload
                  action={apiEndpoint.attachments}
                  headers={authHeader(true)}
                  listType="picture"
                  name="Attachment[file]"
                  fileList={uploadFiles}
                  multiple={true}
                  onChange={onFileChange}
                  data={{ type: 'lock_out', storage: 'filesystem' }}
                  onRemove={handleRemoveAttachment}
                >
                  <Button icon={<UploadOutlined />} size="large">
                    Upload
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
    </AssetModalWrapper>
  )
}

export default EditLockOutHistoryModal;